import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { EMPTY, exhaustMap, tap } from 'rxjs';

import type { Notification, NotificationStore } from '../stores/notification';
import type { NotificationCountService } from './count';

export class NotificationListService extends Service {
  isLoading$ = new LiveData(false);
  notifications$ = new LiveData<Notification[]>([]);
  nextCursor$ = new LiveData<string | undefined>(undefined);
  hasMore$ = new LiveData(true);
  error$ = new LiveData<any>(null);

  readonly PAGE_SIZE = 8;

  constructor(
    private readonly store: NotificationStore,
    private readonly notificationCount: NotificationCountService
  ) {
    super();
  }

  readonly loadMore = effect(
    exhaustMap(() => {
      if (!this.hasMore$.value) {
        return EMPTY;
      }
      return fromPromise(signal =>
        this.store.listNotification(
          {
            first: this.PAGE_SIZE,
            after: this.nextCursor$.value,
          },
          signal
        )
      ).pipe(
        tap(result => {
          if (!result) {
            // If the user is not logged in, we just ignore the result.
            return;
          }
          const { edges, pageInfo } = result;
          this.notifications$.next([
            ...this.notifications$.value,
            ...edges.map(edge => edge.node),
          ]);

          this.hasMore$.next(pageInfo.hasNextPage);
          this.nextCursor$.next(pageInfo.endCursor ?? undefined);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  reset() {
    this.notifications$.setValue([]);
    this.hasMore$.setValue(true);
    this.nextCursor$.setValue(undefined);
    this.isLoading$.setValue(false);
    this.error$.setValue(null);
    this.loadMore.reset();
  }

  ingest(notification: Notification) {
    const previous = this.notifications$.value.filter(
      item => item.id !== notification.id
    );
    this.notifications$.setValue([notification, ...previous]);
  }

  async readNotification(id: string) {
    const previous = this.notifications$.value;
    const notification = previous.find(item => item.id === id);
    if (!notification || notification.read) return;
    this.notifications$.next(
      previous.map(item => (item.id === id ? { ...item, read: true } : item))
    );
    this.notificationCount.setCount(
      Math.max(this.notificationCount.count$.value - 1, 0)
    );
    try {
      await this.store.readNotification(id);
    } catch (error) {
      this.notifications$.next(previous);
      this.notificationCount.setCount(this.notificationCount.count$.value + 1);
      throw error;
    }
  }

  async readAllNotifications() {
    const previous = this.notifications$.value;
    this.notifications$.next(
      previous.map(notification => ({ ...notification, read: true }))
    );
    this.notificationCount.setCount(0);

    try {
      await this.store.readAllNotifications();
    } catch (err) {
      this.notifications$.next(previous);
      this.notificationCount.revalidate();
      throw err;
    }
  }

  async deleteNotification(id: string) {
    const previous = this.notifications$.value;
    const notification = previous.find(item => item.id === id);
    if (!notification) return;
    this.notifications$.next(previous.filter(item => item.id !== id));
    if (!notification.read) {
      this.notificationCount.setCount(
        Math.max(this.notificationCount.count$.value - 1, 0)
      );
    }
    try {
      await this.store.deleteNotification(id);
    } catch (error) {
      this.notifications$.next(previous);
      if (!notification.read) this.notificationCount.revalidate();
      throw error;
    }
  }

  async deleteAllNotifications() {
    this.notifications$.setValue([]);
    this.hasMore$.setValue(false);
    this.notificationCount.setCount(0);
    try {
      await this.store.deleteAllNotifications();
    } catch (error) {
      this.reset();
      this.loadMore();
      this.notificationCount.revalidate();
      throw error;
    }
  }
}
